import React from "react";
import { Card, Row, Col, Button } from "antd";
import ReactECharts from "echarts-for-react";
import { useTranslation } from "react-i18next";
import {
    ArrowLeft,
    Pencil,
    Pause,
    Trash2,
    DollarSign,
    Eye,
    Users,
    MousePointerClick,
    Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Layout from '../../Layout'
import CustomerInteraction from './CustomerInteraction'

const AdsDetail = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // ===== BIG STATS =====
    const bigStats = [
        {
            title: t("ads.detail_spent"),
            value: "1.115.160 đ",
            percent: "-2.2%",
            sub: t("ads.detail_budget_percent", { percent: 2.2 }),
            progress: 2.2,
            icon: DollarSign,
            color: "bg-green-100 text-green-600",
            hasProgress: true,
        },
        {
            title: t("ads.detail_impressions"),
            value: "810.843",
            percent: "+12.5%",
            sub: t("ads.detail_compared_last_week"),
            icon: Eye,
            color: "bg-blue-100 text-blue-600",
        },
        {
            title: t("ads.detail_reach"),
            value: "5.849",
            percent: "+8.3%",
            sub: t("ads.detail_unique_users"),
            icon: Users,
            color: "bg-purple-100 text-purple-600",
        },
        {
            title: t("ads.detail_link_clicks"),
            value: "1.074.818",
            percent: "+5.8%",
            sub: t("ads.detail_click_link"),
            icon: MousePointerClick,
            color: "bg-orange-100 text-orange-600",
        },
    ];

    // ===== SMALL STATS =====
    const smallStats = [
        { title: t("ads.detail_frequency"), value: "1.400000", icon: Activity, color: "bg-green-100 text-green-600", },
        { title: "CPC", value: "104 đ", icon: DollarSign, color: "bg-yellow-100 text-yellow-600", },
        { title: t("ads.detail_clicks"), value: "1.074.818", icon: MousePointerClick, color: "bg-orange-100 text-orange-600", },
        { title: t("ads.detail_cpc_all"), value: "437.100 đ", icon: DollarSign, color: "bg-red-100 text-red-600", },
        { title: "CTR", value: "1.69%", icon: Activity, color: "bg-blue-100 text-blue-600", },
        { title: "CPM", value: "437 đ", icon: DollarSign, color: "bg-purple-100 text-purple-600", },
    ];

    // ===== CHART =====
    const chartOption = {
        tooltip: { trigger: "axis" },

        legend: {
            top: 10,
            icon: "circle",
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 20,
            align: "left",

            textStyle: {
                color: "#6b7280",
                fontSize: 13,
                lineHeight: 14,
            },
        },

        grid: {
            left: 40, // 👈 tăng lên để đủ chỗ hiển thị trục
            right: 10,
            bottom: 20,
            top: 60,
        },

        xAxis: {
            type: "category",
            boundaryGap: false,
            data: ["28/3", "29/3", "30/3", "31/3", "1/4", "2/4", "3/4"],
            axisLine: {
                show: true,
                lineStyle: { color: "#333" },
            },
            axisTick: {
                show: false,
            },
        },

        yAxis: {
            type: "value",

            // 👇 HIỆN SỐ
            axisLabel: {
                show: true,
                color: "#6b7280",
                fontSize: 12,
            },

            // 👇 CÂY THƯỚC (line dọc)
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#d1d5db",
                },
            },

            // 👇 VẠCH NHỎ
            axisTick: {
                show: true,
            },

            // 👇 GRID NGANG
            splitLine: {
                show: true,
                lineStyle: {
                    color: "#f1f5f9",
                    type: "dashed",
                },
            },
        },

        series: [
            {
                name: t("ads.chart_impressions"),
                type: "line",
                smooth: true,
                data: [180, 190, 175, 200, 190, 205, 220],
                lineStyle: { color: "#3b82f6", width: 2 },
                symbol: "circle",
                symbolSize: 6,
            },
            {
                name: t("ads.chart_clicks"),
                type: "line",
                smooth: true,
                data: [5, 6, 4, 7, 6, 7, 8],
                lineStyle: { color: "#22c55e", width: 2 },
                symbol: "circle",
                symbolSize: 6,
            },
            {
                name: t("ads.chart_reach"),
                type: "line",
                smooth: true,
                data: [140, 150, 135, 170, 160, 175, 185],
                lineStyle: { color: "#06b6d4", width: 2 },
                symbol: "circle",
                symbolSize: 6,
            },
            {
                name: t("ads.chart_link_clicks"),
                type: "line",
                smooth: true,
                data: [120, 130, 115, 140, 135, 150, 160],
                lineStyle: { color: "#f59e0b", width: 2 },
                symbol: "circle",
                symbolSize: 6,
            },
        ],
    };

    return (
        <Layout>
            <div className="space-y-4 text-000000e0">

                {/* HEADER */}
                <div className="flex justify-between items-center flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <ArrowLeft className="cursor-pointer" onClick={() => navigate(-1)} />
                        <div className="text-000000e0">
                            <h2 className="text-xl font-semibold">
                                Adsplus 5 - vienthammynkhothi
                            </h2>
                            <div className="text-sm text-gray-500 flex gap-2 mt-1">
                                <span>(act_4806/1121)</span>
                                <span className="bg-green-100 text-green-600 px-2 rounded">
                                    {t("ads.status_active")}
                                </span>
                                <span className="bg-gray-100 px-2 rounded">
                                    Công ty ABC
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <Button icon={<Pencil size={16} />}>{t("ads.btn_edit")}</Button>
                        <Button icon={<Pause size={16} />}>{t("ads.btn_pause")}</Button>
                        <Button danger icon={<Trash2 size={16} />}>{t("ads.btn_delete")}</Button>
                    </div>
                </div>

                {/* BIG STATS */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-000000e0">
                    {bigStats.map((item, i) => {
                        const Icon = item.icon;
                        return (
                            <Card key={i} className="!rounded-xl shadow-sm h-full">

                                <div className="flex justify-between items-start">
                                    <div className={`p-2 rounded-lg ${item.color}`}>
                                        <Icon size={18} />
                                    </div>

                                    <span
                                        className={`text-xs ${item.percent.includes("-")
                                            ? "text-gray-400"
                                            : "text-green-500"
                                            }`}
                                    >
                                        {item.percent}
                                    </span>
                                </div>

                                <div className="text-gray-500 text-sm mt-3 text-000000e0">
                                    {item.title}
                                </div>

                                <div className="text-xl font-semibold mt-1 text-000000e0">
                                    {item.value}
                                </div>

                                {item.sub && (
                                    <div className="text-xs text-gray-400 mt-1 text-000000e0">
                                        {item.sub}
                                    </div>
                                )}

                                {/* progress chỉ card đầu */}
                                {item.hasProgress && (
                                    <div className="mt-2 h-1 bg-gray-200 rounded">
                                        <div
                                            className="h-1 bg-blue-500 rounded"
                                            style={{ width: `${item.progress}%` }}
                                        />
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>

                {/* SMALL STATS */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-000000e0">
                    {smallStats.map((item, i) => {
                        const Icon = item.icon;
                        return (
                            <Card key={i} size="small" className="!rounded-xl h-full">

                                <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded ${item.color}`}>
                                        <Icon size={14} />
                                    </div>
                                    <span className="text-gray-500 text-sm text-000000e0">
                                        {item.title}
                                    </span>
                                </div>

                                <div className="font-semibold mt-1 text-[20px] text-000000e0">
                                    {item.value}
                                </div>

                            </Card>
                        );
                    })}
                </div>

                {/* CONTENT */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                    {/* LEFT */}
                    <Card title={t("ads.post_interaction")} className="!rounded-xl text-000000e0">
                        <div className="text-2xl font-bold text-000000e0">610,643</div>
                        <small className="mb-4 flex ">{t("ads.cost_per_conversation")}</small>

                        <div className="space-y-3 text-sm">

                            <div>
                                <div className="flex justify-between">
                                    <span>{t("ads.comments")}</span>
                                    <span>0</span>
                                </div>
                                <div className="h-[10px] bg-gray-200 rounded mt-1" />
                            </div>

                            <div>
                                <div className="flex justify-between">
                                    <span>{t("ads.shares")}</span>
                                    <span>1</span>
                                </div>
                                <div className="h-[10px] bg-pink-400 rounded mt-1 w-full" />
                            </div>

                            <div>
                                <div className="flex justify-between">
                                    <span>{t("ads.messages")}</span>
                                    <span>1</span>
                                </div>
                                <div className="h-[10px] bg-green-400 rounded mt-1 w-full" />
                            </div>

                            <div>
                                <div className="flex justify-between">
                                    <span>{t("ads.conversation_starts")}</span>
                                    <span>1</span>
                                </div>
                                <div className="h-[10px] bg-blue-400 rounded mt-1 w-full" />
                            </div>

                            <div>
                                <div className="flex justify-between">
                                    <span>{t("ads.new_messaging_contacts")}</span>
                                    <span>1</span>
                                </div>
                                <div className="h-[10px] bg-yellow-400 rounded mt-1 w-full" />
                            </div>

                            <div>
                                <div className="flex justify-between">
                                    <span>{t("ads.new_message_starts")}</span>
                                    <span>1</span>
                                </div>
                                <div className="h-[10px] bg-purple-400 rounded mt-1 w-full" />
                            </div>

                        </div>
                    </Card>

                    {/* RIGHT */}
                    <Card title={t("ads.daily_performance")} className="!rounded-xl h-full">
                        <ReactECharts option={chartOption} style={{ height: 330 }} />
                    </Card>

                </div>

                <CustomerInteraction />
            </div>
        </Layout>
    );
};

export default AdsDetail;